import React, {useState, useEffect} from 'react';
import {Table, OverlayTrigger, Tooltip} from 'react-bootstrap';

interface Column {
  key: string;
  label: string;
  sortable?: boolean;
}

interface GroupedHeader {
  key: string;
  label: string;
  columns: Column[];
}

interface DataTableProps {
  headers: GroupedHeader[];
  rows?: any[];
  onAction?: (action: string, row: any) => void;
  onSortAction?: (row: any, action?: string) => void;
  isLogTable?: boolean;
}

const DataTable: React.FC<DataTableProps> = ({
  headers,
  rows = [],
  onAction,
  onSortAction,
  isLogTable = false,
}) => {
  const [sortedRows, setSortedRows] = useState<any[]>(rows);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ASC' | 'DESC' | null;
  }>({
    key: '',
    direction: null,
  });

  useEffect(() => {
    setSortedRows([...rows]);
  }, [rows]);

  const handleSort = (key: string, direction: 'ASC' | 'DESC') => {
    const sorted = [...sortedRows].sort((a, b) => {
      if (direction === 'ASC') return a[key] > b[key] ? 1 : -1;
      if (direction === 'DESC') return a[key] < b[key] ? 1 : -1;
      return 0;
    });
    setSortedRows(sorted);
    setSortConfig({key, direction});
    onSortAction?.({key, direction});
  };

  const renderCellValue = (row: any, key: string) => {
    const value = row[key];
    const displayValue = value || '-';

    if (typeof value === 'object' && value !== null) {
      return Object.keys(value).map((actionKey) => {
        if (actionKey === 'Edit' && row.actions?.Edit) {
          return (
            <OverlayTrigger
              key='edit'
              placement='bottom'
              overlay={<Tooltip id={`edit-tooltip-${row.id}`}>Edit</Tooltip>}
            >
              <span
                className='material-icons color_brand'
                onClick={() => onAction?.('Edit', row)}
                style={{cursor: 'pointer'}}
              >
                edit
              </span>
            </OverlayTrigger>
          );
        }
        return null;
      });
    }

    return (
      <OverlayTrigger
        placement='auto'
        overlay={
          displayValue && displayValue.length > (isLogTable ? 60 : 15) ? (
            <Tooltip id={`tooltip-${key}-${row.id}`}>{displayValue}</Tooltip>
          ) : (
            <></>
          )
        }
      >
        <span
          className='d-inline-block text-truncate'
          style={{maxWidth: isLogTable ? 'none' : '100%', cursor: 'pointer'}}
        >
          {displayValue}
        </span>
      </OverlayTrigger>
    );
  };

  return (
    <div className='table-responsive'>
      <Table className='highlighter'>
        <thead>
          <tr className='firstRow'>
            {headers.map((group) => (
              <th
                className='highlightBlue'
                key={group.key}
                colSpan={group.columns.length}
                style={{backgroundColor: '#e0f7fa', textAlign: 'center'}}
              >
                {group.label}
              </th>
            ))}
          </tr>
          <tr>
            {headers.flatMap((group) =>
              group.columns.map((col) => (
                <th
                  key={col.key}
                  style={{cursor: col.sortable ? 'pointer' : 'default'}}
                >
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    <span>{col.label}</span>
                    {col.sortable && (
                      <span style={{marginLeft: '4px', cursor: 'pointer'}}>
                        <span
                          className='material-icons'
                          style={{fontSize: '18px'}}
                          onClick={() => handleSort(col.key, 'ASC')}
                        >
                          arrow_drop_up
                        </span>
                        <span
                          className='material-icons'
                          style={{fontSize: '18px'}}
                          onClick={() => handleSort(col.key, 'DESC')}
                        >
                          arrow_drop_down
                        </span>
                      </span>
                    )}
                  </div>
                </th>
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {sortedRows.length > 0 ? (
            sortedRows.map((row, index) => (
              <tr
                key={row.id || index}
                style={{backgroundColor: index % 2 === 1 ? 'white' : '#f2f2f2'}}
              >
                {headers.flatMap((group) =>
                  group.columns.map((col) => (
                  <td
  key={col.key}
  style={{ padding: "8px" }}
  className="highlighterRow"
>
  {col.key === "emailAddress" ? (
    row[col.key] ? (
      <a
        href={`mailto:${row[col.key]}`}
        style={{
          color: "#0d6efd",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        {row[col.key]}
      </a>
    ) : (
      ""
    )
  ) : (
    renderCellValue(row, col.key)
  )}
</td>
                  )),
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={headers.reduce(
                  (sum, group) => sum + group.columns.length,
                  0,
                )}
                style={{textAlign: 'center'}}
              >
                <span className='text-danger'>No Record Found</span>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default DataTable;
